import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS, SERVICES } from "@/data/site";
import { useLang, useSite } from "@/i18n";
import { trackConversion, trackingPayload } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIXED_SUBJECTS = ["B2B / Producție de serie", "Colaborare arhitect", "Altele"];

export default function ContactForm({ defaultSubject = "" }) {
  const { t, lang } = useLang();
  const site = useSite();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: defaultSubject,
    message: "",
  });
  const [isCompany, setIsCompany] = useState(false);
  const [cui, setCui] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyInfo, setCompanyInfo] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const lookup = async () => {
    if (!cui.trim()) {
      toast.error("Introdu mai întâi CUI-ul firmei.");
      return;
    }
    setLookupLoading(true);
    try {
      const { data } = await axios.post(`${API}/company-lookup`, { cui });
      setCompanyName(data.name);
      setCompanyInfo(
        `${data.address}${data.vat_registered ? " · Plătitor de TVA" : " · Neplătitor de TVA"}`
      );
      toast.success("Firmă găsită în registrul ANAF.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "CUI negăsit în registrul ANAF.");
      setCompanyInfo("");
    } finally {
      setLookupLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        ...(isCompany ? { cui, company_name: companyName } : {}),
        ...trackingPayload(lang),
      };
      const { data } = await axios.post(`${API}/contact`, payload);
      trackConversion();
      toast.success(data.message || "Mesaj trimis cu succes!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setIsCompany(false);
      setCui("");
      setCompanyName("");
      setCompanyInfo("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "A apărut o eroare. Încearcă din nou sau sună-ne.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-ink/15 bg-surface px-4 py-3.5 text-base font-light text-ink placeholder-ink/35 outline-none transition-colors duration-300 focus:border-gold";

  const labelCls = "kicker mb-2.5 block";

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{t("form_name")}</label>
          <input required minLength={2} value={form.name} onChange={set("name")} data-testid="contact-name-input" placeholder={t("form_name_ph")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("form_email")}</label>
          <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" placeholder={t("form_email_ph")} className={inputCls} />
        </div>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{t("form_phone")}</label>
          <input value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" placeholder={t("phone_ph")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("form_subject")}</label>
          <select value={form.subject} onChange={set("subject")} data-testid="contact-subject-select" className={inputCls}>
            <option value="">{t("form_subject_ph")}</option>
            <optgroup label={t("nav_services")}>
              {SERVICES.map((s, i) => (
                <option key={s.slug} value={s.name}>{site.services[i].name}</option>
              ))}
            </optgroup>
            <optgroup label={t("nav_products")}>
              {PRODUCTS.map((p, i) => (
                <option key={p.slug} value={p.name}>{site.products[i].name}</option>
              ))}
            </optgroup>
            <option value="B2B / Producție de serie">{t("hdr_b2b_t")}</option>
            <option value="Colaborare arhitect">{t("hdr_arch_t")}</option>
            {defaultSubject && !FIXED_SUBJECTS.includes(defaultSubject) && !SERVICES.some((s) => s.name === defaultSubject) && !PRODUCTS.some((p) => p.name === defaultSubject) && (
              <option value={defaultSubject}>{defaultSubject}</option>
            )}
            <option value="Altele">{t("other")}</option>
          </select>
        </div>
      </div>

      <div className="border border-ink/10 bg-base p-5">
        <label className="flex cursor-pointer items-center gap-3" data-testid="contact-company-toggle">
          <input
            type="checkbox"
            checked={isCompany}
            onChange={(e) => setIsCompany(e.target.checked)}
            className="h-4 w-4 shrink-0 accent-[#24435C]"
          />
          <span className="text-sm font-normal text-ink/80">{t("form_company")}</span>
        </label>

        {isCompany && (
          <div className="mt-5 space-y-4">
            <div>
              <label className={labelCls}>{t("form_cui")}</label>
              <div className="flex gap-3">
                <input
                  value={cui}
                  onChange={(e) => setCui(e.target.value)}
                  required
                  placeholder={t("form_cui_ph")}
                  className={inputCls}
                  data-testid="contact-cui-input"
                />
                <button
                  type="button"
                  onClick={lookup}
                  disabled={lookupLoading || lang !== "ro"}
                  data-testid="contact-cui-lookup-button"
                  className={`shrink-0 border border-ink/20 px-4 text-[11px] font-semibold uppercase tracking-widest text-ink/70 transition-colors duration-300 hover:border-ink hover:text-ink disabled:opacity-50 ${lang !== "ro" ? "hidden" : ""}`}
                >
                  {lookupLoading ? t("form_lookup_busy") : t("form_lookup")}
                </button>
              </div>
            </div>
            <div>
              <label className={labelCls}>{t("form_company_name")}</label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                placeholder={t("form_company_ph")}
                className={inputCls}
                data-testid="contact-company-name-input"
              />
              {companyInfo && (
                <p className="mt-2 text-xs leading-relaxed text-ink/50" data-testid="contact-company-info">
                  {companyInfo}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <label className={labelCls}>{t("form_message")}</label>
        <textarea required minLength={10} rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" placeholder={t("form_message_ph")} className={`${inputCls} resize-none`} />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit-button"
        className="group flex items-center gap-2 bg-ink px-7 py-3.5 text-[14px] font-semibold text-base transition-colors duration-300 hover:bg-gold disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? t("form_sending") : t("form_submit")}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </button>
    </form>
  );
}
